import useIsOverflowing from '@app/hooks/useIsOverflowing';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Icons,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React from 'react';
import { reactionData, reactionsForItem } from '@core/reactions/selectors';
import { commentCount } from '@core/selectors/comments';
import { isInstructor } from '@core/selectors/classroom';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import type { AulaDispatch, AulaState, Post } from '@core/types/state';
import { useDispatch, useSelector } from 'react-redux';
import { goToPost } from '@app/actions/navigation';
import { online as isOnline } from '@core/selectors/live';
import {
  getFullName,
  getUserInitials,
} from '@components/presentational/Home/widgets/RecentUpdates/recentUpdates.utils';
import formatDate from '@core/utils/formatDate';
import { postContentParser } from '@components/presentational/Home/widgets/RecentUpdates/postContentParser';
import { currentUser } from '@core/selectors/user';
import { createStyles } from './PinnedPost.styles';
import SidebarPinnedPostOverview from './PinnedPostOverview';

type Props = {
  post: Post;
};

const SidebarPinnedPost = ({ post }: Props) => {
  const [nameRef, isOverflowing] = useIsOverflowing<HTMLDivElement>();
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTooltipDisabled = !isDesktop || !isOverflowing;

  const dispatch = useDispatch<AulaDispatch>();
  const reactionsCount = useSelector((state: AulaState) =>
    reactionsForItem(reactionData(state), post.objectId).reduce(
      (total, reaction) => total + (reaction.count || 0),
      0
    )
  );
  const commentsCount = useSelector((state: AulaState) =>
    commentCount(state, post.objectId, CommentsEntityTypes.post)
  );
  const { user } = post;
  const isEducator = useSelector((state: AulaState) =>
    isInstructor(state, user.objectId)
  );
  const authorName = getFullName(user);
  const handleOpen = () =>
    dispatch(goToPost({ classRoom: post.classRoom, objectId: post.objectId }));

  const initials = getUserInitials(user);
  const timestamp = formatDate(post.scheduledFor || post.createdAt);
  const { text, imagesCount, filesCount, integrationsCount, linksCount } =
    postContentParser(post.content);
  const attachmentCount =
    imagesCount + filesCount + integrationsCount + linksCount;
  const cardSummary = [
    `Pinned post by ${authorName}${isEducator ? ', Educator' : ''}.`,
    `${timestamp}.`,
    text,
    attachmentCount > 0
      ? `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}.`
      : '',
    `${reactionsCount} reaction${reactionsCount === 1 ? '' : 's'}.`,
    `${commentsCount} comment${commentsCount === 1 ? '' : 's'}.`,
  ]
    .filter(Boolean)
    .join(' ');

  const loggedInUser = useSelector(currentUser);
  const isAuthorOnline = useSelector((state: AulaState) =>
    isOnline(state, user.objectId)
  );
  const isLoggedInUser = loggedInUser?.objectId === user?.objectId;

  const statItems = [
    {
      label: 'reaction',
      count: reactionsCount,
      Icon: Icons.EmojiEmotionsOutlined,
    },
    {
      label: 'comment',
      count: commentsCount,
      Icon: Icons.ModeCommentOutlined,
    },
  ];

  return (
    <Box
      sx={styles.card}
      component="article"
      tabIndex={0}
      aria-label={cardSummary}
      onClick={handleOpen}
    >
      <Box sx={styles.author}>
        <Box sx={styles.avatarWrapper}>
          <Avatar
            altText={authorName}
            fallbackText={initials}
            kind="circular"
            src={user?.avatar}
            sx={styles.avatar}
          />
          {isAuthorOnline && !isLoggedInUser && <Box sx={styles.onlineBadge} />}
        </Box>
        <Box sx={styles.authorDetails}>
          <Box sx={styles.authorNameRow}>
            <Tooltip
              title={authorName}
              disableFocusListener={isTooltipDisabled}
              disableHoverListener={isTooltipDisabled}
              disableTouchListener={isTooltipDisabled}
            >
              <Box ref={nameRef} sx={styles.authorName}>
                {authorName}
              </Box>
            </Tooltip>
            {isEducator && (
              <Chip label="Educator" size="small" sx={styles.roleBadge} />
            )}
          </Box>
          <Typography sx={styles.timestamp}>{timestamp}</Typography>
        </Box>
      </Box>
      <SidebarPinnedPostOverview content={post.content} />
      <Box sx={styles.footer}>
        <Box sx={styles.stats}>
          {statItems.map(({ label, count, Icon }) => (
            <Box
              key={label}
              aria-label={`${count} ${label}${count === 1 ? '' : 's'}`}
              sx={styles.stat}
            >
              <Box sx={styles.statIcon}>
                <Icon color="inherit" aria-hidden="true" />
              </Box>
              <Typography
                variant="body2"
                aria-hidden="true"
                sx={styles.statValue}
              >
                {count}
              </Typography>
            </Box>
          ))}
        </Box>
        <Button
          type="button"
          className="pinnedPostReadMore"
          aria-label={`Read more of ${authorName}'s post`}
          sx={styles.readMore}
        >
          Read more
          <Icons.ArrowForward sx={{ height: '18px', width: '18px' }} />
        </Button>
      </Box>
    </Box>
  );
};

export default SidebarPinnedPost;
